/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNavigate, useLocation } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Select,
  SelectItem,
  addToast,
  Switch,
  Spinner,
  DatePicker,
} from '@heroui/react';
import { useTranslation } from 'react-i18next';
import {
  FaArrowLeft,
  FaPaperPlane,
  FaPlus,
  FaMapLocationDot,
  FaRegClock,
  FaUserTie,
  FaBriefcase,
  FaCalendarDays,
  FaCircleCheck,
  FaXmark,
  FaCircleInfo,
} from 'react-icons/fa6';
import { useRef, useState } from 'react';
import { parseDate, parseDateTime } from '@internationalized/date';
import CustomButton from '../../components/CustomButton';
import CustomInput from '../../components/CustomInput';
import { FEATURE_FLAGS, useFeatureFlag } from '../../../config/featureFlags';
import { useQueries, useMutation } from '@tanstack/react-query';
import { uploadToS3 } from '../Customer/aws/s3FileUpload';
import { TimeframePreference } from '../../../utils/type';
import { all_routes } from '../../../core/data/routes/all_routes';
import {
  PromptAnswer,
  CreateBroadcastJobPayload,
  createBroadcastJob,
} from '../../../service/jobService';
import CustomTextArea from '../../components/common/ui/CustomTextArea';
import AddOnSelector from '../../components/AddOnSelector';
import { getServiceByQuery } from '../../../service/serviceService';
import ModernCard from '../../components/common/ui/ModernCard';
import CustomChip from '../../components/CustomChip';

interface JobRequestFormValues {
  scopeDescription: string;
  location?: string;
  timeframePreference: TimeframePreference;
  budgetMin?: string;
  budgetMax?: string;
}

interface PromptFieldState {
  [promptId: string]: string | number | boolean | string[];
}

interface ScopePromptInfo {
  promptId: string;
  label: string;
  type?: string;
  required?: boolean;
  options?: Array<{ value: string; label: string }>;
  placeholder?: string;
  isLegacy?: boolean;
}

const schema = yup.object({
  scopeDescription: yup
    .string()
    .required('Describe what you need')
    .min(20, 'Please add a bit more detail (min 20 characters)')
    .max(2000, 'Keep the description under 2000 characters'),
  location: yup.string().max(200, 'Location is too long'),
  timeframePreference: yup
    .mixed<TimeframePreference>()
    .oneOf(['asap', '2weeks', 'month', 'flexible'], 'Select a timeframe')
    .required('Select a timeframe'),
  budgetMin: yup
    .string()
    .optional()
    .test(
      'valid-budget',
      'Enter a valid number',
      (v) => !v || !Number.isNaN(Number(v))
    ),
  budgetMax: yup
    .string()
    .optional()
    .test(
      'valid-budget',
      'Enter a valid number',
      (v) => !v || !Number.isNaN(Number(v))
    )
    .test('gt-min', 'Max should be greater than min', function (v) {
      const min = this.parent?.budgetMin;
      if (!v || !min) return true;
      return Number(v) > Number(min);
    }),
});

const TIMEFRAME_OPTIONS: { value: TimeframePreference; label: string }[] = [
  { value: 'asap', label: 'As soon as possible' },
  { value: '2weeks', label: 'Within 2 weeks' },
  { value: 'month', label: 'Within a month' },
  { value: 'flexible', label: 'Flexible — let me discuss' },
];

const BatchQuoteRequestForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const navigationState = location.state as {
    services?: { serviceId: string; providerId: string }[];
  } | null;

  const services = navigationState?.services || [];
  const serviceId = services[0]?.serviceId;

  // Active Service Tab State
  const [activeServiceId, setActiveServiceId] = useState<string>(
    serviceId || ''
  );

  const broadcastMutation = useMutation({
    mutationFn: (data: CreateBroadcastJobPayload) => createBroadcastJob(data),
    onSuccess: () => {
      addToast({
        title: 'Quote request sent',
        description:
          'Requests sent to all selected providers. Check back soon for their quotes.',
        radius: 'md',
        color: 'success',
      });
      navigate(all_routes.customerJobRequests);
    },
    onError: (err: any) => {
      const isDuplicateBusiness = (err?.response?.data?.errors || []).some(
        (e: any) => e?.field === 'DUPLICATE_BUSINESS'
      );
      const message = isDuplicateBusiness
        ? 'You can only ask each business once — please review your selections and try again.'
        : err?.response?.data?.message || 'Failed to send quote requests';
      addToast({
        title: 'Error',
        description: message,
        radius: 'md',
        color: 'danger',
      });
    },
  });

  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedAdditionalServiceIds, setSelectedAdditionalServiceIds] =
    useState<Record<string, string[]>>({});
  const [promptFieldValues, setPromptFieldValues] = useState<
    Record<string, PromptFieldState>
  >({});

  const quotationV2Enabled = useFeatureFlag(FEATURE_FLAGS.quotationFlowV2);

  const serviceQueries = useQueries({
    queries: services.map((service) => ({
      queryKey: ['broadcast-service', service.serviceId],
      queryFn: () =>
        getServiceByQuery()({
          filters: { serviceId: service.serviceId },
          fullProfile: true,
          ownerId: '',
          categoryProjection: 'categoryName',
          withMongoId: false,
        }),
      enabled: Boolean(service.serviceId),
      staleTime: 5 * 60 * 1000,
    })),
  });

  const requestedServices = serviceQueries
    .map((query) => query.data?.services?.[0] || query.data)
    .filter(Boolean);

  const responseSlaDays = Math.max(
    ...requestedServices.map((service) =>
      typeof service.respondsWithinDays === 'number'
        ? service.respondsWithinDays
        : 2
    ),
    2
  );

  const normalizeOptions = (
    options: any[] | undefined
  ): Array<{ value: string; label: string }> => {
    if (!options || options.length === 0) return [];
    return options.map((opt) => {
      if (typeof opt === 'string') {
        return { value: opt, label: opt };
      }
      // already an object shape, but guard against missing fields
      return {
        value: opt.value ?? opt.label ?? String(opt),
        label: opt.label ?? opt.value ?? String(opt),
      };
    });
  };

  const buildScopePromptsInfo = (
    prompts: any[] | undefined
  ): ScopePromptInfo[] => {
    if (!prompts || prompts.length === 0) return [];
    return prompts.map((prompt, idx) => {
      if (typeof prompt === 'string') {
        return {
          promptId: `legacy_${idx}`,
          label: prompt,
          type: 'SHORT_TEXT',
          required: false,
          isLegacy: true,
        };
      }
      return {
        promptId: prompt.promptId || `prompt_${idx}`,
        label: prompt.label || prompt.promptText || '',
        type: prompt.type || 'SHORT_TEXT',
        required: prompt.required || false,
        options: normalizeOptions(prompt.options),
        placeholder: prompt.placeholder,
        isLegacy: false,
      };
    });
  };

  const notify = (
    title: string,
    description: string,
    color: 'warning' | 'danger'
  ) => {
    if (!quotationV2Enabled) {
      window.alert(description);
      return;
    }
    addToast({ title, description, radius: 'md', color });
  };

  const form = useForm<JobRequestFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      scopeDescription: '',
      location: '',
      timeframePreference: 'asap',
      budgetMin: '',
      budgetMax: '',
    },
  });

  if (!serviceId || services.length === 0) {
    navigate(all_routes.searchList);
    return null;
  }

  /**
   * Corrected Completion Check Logic:
   * A service is only marked completed if the user has answered all required scope prompts,
   * OR if there are no required prompts and the user has selected at least one add-on or filled a prompt field.
   */
  const isServiceCompleted = (service: any) => {
    const prompts = buildScopePromptsInfo(service.scopePrompts);
    const valuesForService = promptFieldValues[service.serviceId] || {};
    const selectedAddons =
      selectedAdditionalServiceIds[service.serviceId] || [];

    const requiredPrompts = prompts.filter((p) => p.required);

    if (requiredPrompts.length > 0) {
      return !requiredPrompts.some((prompt) => {
        const val = valuesForService[prompt.promptId];
        if (val === undefined || val === null) return true;
        if (Array.isArray(val)) return val.length === 0;
        if (typeof val === 'string') return val.trim().length === 0;
        return false;
      });
    }

    // If there are no required prompts, check if user interacted with optional prompts or add-ons
    const hasAnsweredAnyPrompt = Object.values(valuesForService).some((val) => {
      if (val === undefined || val === null) return false;
      if (Array.isArray(val)) return val.length > 0;
      if (typeof val === 'string') return val.trim().length > 0;
      return Boolean(val);
    });

    return hasAnsweredAnyPrompt || selectedAddons.length > 0;
  };

  const activeService =
    requestedServices.find((s) => s.serviceId === activeServiceId) ||
    requestedServices[0];

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploaded: string[] = [];
      const remaining = Math.max(0, 6 - photoUrls.length);
      const slice = Array.from(files).slice(0, remaining);
      for (const file of slice) {
        const url = await uploadToS3(file, 'job-request-photos');
        uploaded.push(url);
      }
      if (uploaded.length === 0) {
        notify(
          'Photo limit reached',
          'You can attach up to 6 photos.',
          'warning'
        );
      } else {
        setPhotoUrls((prev) => [...prev, ...uploaded].slice(0, 6));
      }
    } catch (err) {
      console.error('Photo upload failed:', err);
      notify(
        'Upload failed',
        'One or more photos failed to upload. Please try again.',
        'danger'
      );
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onSubmit = (values: JobRequestFormValues) => {
    // Build services array WITH per-service config
    const enrichedServices: CreateBroadcastJobPayload['services'] = services
      .map((service) => {
        const prompts = buildScopePromptsInfo(
          requestedServices.find((s) => s.serviceId === service.serviceId)
            ?.scopePrompts
        );

        const valuesForService = promptFieldValues[service.serviceId] || {};

        // Validate required prompts for THIS service
        const missingRequired = prompts.find((prompt) => {
          if (!prompt.required) return false;
          const value = valuesForService[prompt.promptId];
          if (value === undefined || value === null) return true;
          if (Array.isArray(value)) return value.length === 0;
          if (typeof value === 'string') return value.trim().length === 0;
          return false;
        });

        if (missingRequired) {
          setActiveServiceId(service.serviceId);
          notify(
            'Missing required answer',
            `Please answer "${missingRequired.label}" for ${
              requestedServices.find((s) => s.serviceId === service.serviceId)
                ?.serviceTitle
            }`,
            'warning'
          );
          return null;
        }

        // Build prompt answers FOR THIS SERVICE ONLY
        const servicePromptAnswers: PromptAnswer[] = prompts.map((prompt) => ({
          promptId: prompt.promptId,
          label: prompt.label,
          type: prompt.type || 'SHORT_TEXT',
          answer: valuesForService[prompt.promptId] ?? '',
        }));

        return {
          serviceId: service.serviceId,
          providerId: service.providerId,
          additionalServiceIds:
            selectedAdditionalServiceIds[service.serviceId] || [],
          promptAnswers: servicePromptAnswers,
        };
      })
      .filter(
        (service): service is NonNullable<typeof service> => service !== null
      );

    if (enrichedServices.length !== services.length) return; // Validation failed

    const payload: CreateBroadcastJobPayload = {
      services: enrichedServices,
      scopeDescription: values.scopeDescription,
      photos: photoUrls.length > 0 ? photoUrls : undefined,
      location: values.location?.trim() || undefined,
      timeframePreference: values.timeframePreference,
      budgetMin: values.budgetMin ? Number(values.budgetMin) : undefined,
      budgetMax: values.budgetMax ? Number(values.budgetMax) : undefined,
    };

    broadcastMutation.mutate(payload);
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const setPromptValue = (
    serviceId: string,
    promptId: string,
    value: string | number | boolean | string[]
  ) => {
    setPromptFieldValues((current) => ({
      ...current,
      [serviceId]: {
        ...current[serviceId],
        [promptId]: value,
      },
    }));
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 py-6 space-y-6">
      {/* Back button */}
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="group flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-primary transition-colors cursor-pointer w-fit"
      >
        <div className="p-2 rounded-xl bg-slate-100 group-hover:bg-primary/10 transition-colors">
          <FaArrowLeft size={12} />
        </div>
        <span>{t('quote.back', 'Back to Search Results')}</span>
      </button>

      {/* Hero Header Banner */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#163544] via-[#1a4154] to-[#0095B7] text-white p-6 md:p-8 shadow-md">
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1.5 max-w-2xl">
            <div className="flex items-center gap-2">
              <CustomChip
                label="Multi-Provider Request"
                color="primary"
                variant="flat"
                size="sm"
                className="bg-white/10 text-white font-bold tracking-wide uppercase text-[10px]"
              />
              <span className="text-xs font-semibold text-slate-200/80">
                {services.length} Providers Selected
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-white">
              {t('quote.request_title', 'Request Quotes from Local Pros')}
            </h1>
            <p className="text-xs md:text-sm text-slate-200/90 leading-relaxed font-medium">
              {t(
                'quote.request_subtitle',
                'Select each service tab below to customize extras and answer scope details for each provider.'
              )}
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <CustomButton
              label="Submit Request to All"
              color="primary"
              variant="solid"
              startContent={<FaPaperPlane size={14} />}
              onPress={handleSubmit(onSubmit)}
              isLoading={broadcastMutation.isPending}
              isDisabled={broadcastMutation.isPending}
              className="bg-white text-primary font-bold hover:bg-slate-100 px-6 py-3 shadow-md"
            />
          </div>
        </div>
      </div>

      {/* Informative Guidance Banner */}
      <div className="flex items-center gap-3 bg-primary/10 border border-primary/20 p-4 rounded-2xl text-primary text-xs font-semibold shadow-2xs">
        <FaCircleInfo size={18} className="shrink-0 text-primary" />
        <p>
          Please switch between the service tabs below to customize add-ons and
          fill in specific requirements for each selected service.
        </p>
      </div>

      {/* Service Tabs Navigation Row */}
      <div className="w-full overflow-x-auto no-scrollbar pb-1">
        <div className="flex gap-2.5 bg-slate-100/80 p-1.5 rounded-2xl w-max min-w-full border border-slate-200/60">
          {requestedServices.map((svc, idx) => {
            const isSelected =
              svc.serviceId === (activeService?.serviceId || activeServiceId);
            const completed = isServiceCompleted(svc);

            return (
              <button
                key={svc.serviceId || idx}
                type="button"
                onClick={() => setActiveServiceId(svc.serviceId)}
                className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-bold transition-all shrink-0 cursor-pointer ${
                  isSelected
                    ? 'bg-white text-primary border border-primary shadow-sm'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/50'
                }`}
              >
                <span className="truncate max-w-[200px]">
                  {svc.serviceTitle || `Service #${idx + 1}`}
                </span>
                {completed ? (
                  <FaCircleCheck
                    className="text-emerald-500 shrink-0"
                    size={14}
                  />
                ) : (
                  <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0 animate-pulse" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <FormProvider {...form}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Main Focused Service Area */}
          <div className="lg:col-span-8 space-y-6">
            {activeService && (
              <ModernCard
                title={
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-bold">
                        <FaBriefcase size={18} />
                      </div>
                      <div>
                        <p className="text-base font-bold text-slate-800">
                          {activeService.serviceTitle ||
                            'Selected Service Details'}
                        </p>
                        {activeService.businessName && (
                          <p className="text-xs text-slate-400 flex items-center gap-1 font-medium mt-0.5">
                            <FaUserTie size={11} /> {activeService.businessName}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="px-2">
                      {isServiceCompleted(activeService) ? (
                        <CustomChip
                          label="Details Completed"
                          color="success"
                          variant="flat"
                          size="sm"
                          className="font-bold text-[10px]"
                        />
                      ) : (
                        <CustomChip
                          label="Pending Details"
                          color="warning"
                          variant="flat"
                          size="sm"
                          className="font-bold text-[10px]"
                        />
                      )}
                    </div>
                  </div>
                }
              >
                <div className="space-y-6">
                  {/* Standard Inclusions */}
                  {activeService.includes?.length > 0 && (
                    <div className="bg-slate-50/80 p-4 rounded-xl border border-slate-200/60 space-y-2">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        {t('quote.whats_included', 'Standard Inclusions')}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {activeService.includes.map(
                          (item: string, idx: number) => (
                            <div
                              key={idx}
                              className="flex items-center gap-2 text-xs font-medium text-slate-700 bg-white p-2 rounded-lg border border-slate-100"
                            >
                              <FaCircleCheck
                                className="text-emerald-500 shrink-0"
                                size={13}
                              />
                              <span className="truncate">{item}</span>
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  )}

                  {/* Optional Add-Ons Selector */}
                  {activeService.additionalServices?.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        {t(
                          'quote.optional_extras',
                          'Customize with Optional Extras'
                        )}
                      </p>
                      <AddOnSelector
                        addons={activeService.additionalServices}
                        selectedIds={
                          selectedAdditionalServiceIds[
                            activeService.serviceId
                          ] || []
                        }
                        onChange={(ids) =>
                          setSelectedAdditionalServiceIds((current) => ({
                            ...current,
                            [activeService.serviceId]: ids,
                          }))
                        }
                      />
                    </div>
                  )}

                  {/* Service Specific Scope Questions */}
                  {buildScopePromptsInfo(activeService.scopePrompts).length >
                    0 && (
                    <div className="pt-4 border-t border-slate-100 space-y-4">
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Specific Questions for this Service
                      </p>
                      {buildScopePromptsInfo(activeService.scopePrompts).map(
                        (prompt) => {
                          const valuesForService =
                            promptFieldValues[activeService.serviceId] || {};

                          return (
                            <div key={prompt.promptId} className="space-y-1.5">
                              <label className="text-xs font-bold text-slate-700 block">
                                {prompt.label}
                                {prompt.required && (
                                  <span className="text-danger"> *</span>
                                )}
                              </label>

                              {prompt.type === 'LONG_TEXT' && (
                                <CustomTextArea
                                  label=""
                                  placeholder={
                                    prompt.placeholder || 'Type here...'
                                  }
                                  value={String(
                                    valuesForService[prompt.promptId] || ''
                                  )}
                                  onChange={(e) =>
                                    setPromptValue(
                                      activeService.serviceId,
                                      prompt.promptId,
                                      e.target.value
                                    )
                                  }
                                  classNames={{
                                    input: 'text-xs',
                                  }}
                                />
                              )}

                              {prompt.type === 'SHORT_TEXT' && (
                                <CustomInput
                                  type="text"
                                  placeholder={
                                    prompt.placeholder || 'Type answer...'
                                  }
                                  value={String(
                                    valuesForService[prompt.promptId] || ''
                                  )}
                                  onValueChange={(value) =>
                                    setPromptValue(
                                      activeService.serviceId,
                                      prompt.promptId,
                                      value
                                    )
                                  }
                                  classNames={{
                                    input: 'text-xs',
                                  }}
                                />
                              )}

                              {prompt.type === 'NUMBER' && (
                                <CustomInput
                                  type="number"
                                  placeholder={
                                    prompt.placeholder || 'Enter number...'
                                  }
                                  value={String(
                                    valuesForService[prompt.promptId] || ''
                                  )}
                                  onValueChange={(value) =>
                                    setPromptValue(
                                      activeService.serviceId,
                                      prompt.promptId,
                                      value ? Number(value) : ''
                                    )
                                  }
                                  classNames={{
                                    input: 'text-xs',
                                  }}
                                />
                              )}

                              {prompt.type === 'BOOLEAN' && (
                                <Switch
                                  isSelected={Boolean(
                                    valuesForService[prompt.promptId]
                                  )}
                                  onChange={(e) =>
                                    setPromptValue(
                                      activeService.serviceId,
                                      prompt.promptId,
                                      e.target.checked
                                    )
                                  }
                                  size="sm"
                                />
                              )}

                              {(prompt.type === 'SINGLE_SELECT' ||
                                prompt.type === 'MULTI_SELECT') && (
                                <Select
                                  selectionMode={
                                    prompt.type === 'MULTI_SELECT'
                                      ? 'multiple'
                                      : 'single'
                                  }
                                  size="sm"
                                  placeholder={
                                    prompt.placeholder || 'Select option'
                                  }
                                  selectedKeys={
                                    Array.isArray(
                                      valuesForService[prompt.promptId]
                                    )
                                      ? new Set(
                                          valuesForService[
                                            prompt.promptId
                                          ] as string[]
                                        )
                                      : valuesForService[prompt.promptId]
                                        ? new Set([
                                            String(
                                              valuesForService[prompt.promptId]
                                            ),
                                          ])
                                        : new Set()
                                  }
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setPromptValue(
                                      activeService.serviceId,
                                      prompt.promptId,
                                      prompt.type === 'MULTI_SELECT'
                                        ? value
                                          ? value.split(',')
                                          : []
                                        : value
                                    );
                                  }}
                                  classNames={{
                                    trigger:
                                      'bg-white hover:bg-white data-[hover=true]:bg-white data-[open=true]:bg-white border border-slate-200 shadow-sm',
                                    value: 'text-slate-700 text-xs',
                                  }}
                                >
                                  {prompt.options?.map(
                                    (option: {
                                      value: string;
                                      label: string;
                                    }) => (
                                      <SelectItem key={option.value}>
                                        {option.label}
                                      </SelectItem>
                                    )
                                  ) || null}
                                </Select>
                              )}

                              {(prompt.type === 'DATE' ||
                                prompt.type === 'DATETIME') && (
                                <DatePicker
                                  size="sm"
                                  granularity={
                                    prompt.type === 'DATETIME'
                                      ? 'minute'
                                      : 'day'
                                  }
                                  hideTimeZone={prompt.type === 'DATETIME'}
                                  value={(() => {
                                    const raw =
                                      valuesForService[prompt.promptId];
                                    if (!raw) return undefined;
                                    const str = String(raw);
                                    if (prompt.type === 'DATETIME') {
                                      try {
                                        return parseDateTime(
                                          str.length > 10
                                            ? str
                                            : `${str}T00:00:00`
                                        );
                                      } catch {
                                        return parseDate(str.substring(0, 10));
                                      }
                                    }
                                    return parseDate(str.substring(0, 10));
                                  })()}
                                  onChange={(date) => {
                                    setPromptValue(
                                      activeService.serviceId,
                                      prompt.promptId,
                                      date ? date.toString() : ''
                                    );
                                  }}
                                  classNames={{
                                    inputWrapper:
                                      'bg-white hover:bg-white data-[hover=true]:bg-white data-[open=true]:bg-white border border-slate-200 shadow-sm',
                                    input: 'text-xs text-slate-700',
                                  }}
                                />
                              )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                </div>
              </ModernCard>
            )}

            {/* Global General Description & Logistics Card */}
            <ModernCard
              title={
                <span className="font-bold text-slate-800">
                  Global Project Description & Logistics
                </span>
              }
            >
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                {/* Description */}
                <div>
                  <CustomTextArea
                    {...register('scopeDescription')}
                    variant="flat"
                    label={t('quote.scope_label', 'Describe what you need')}
                    isRequired
                    minRows={5}
                    placeholder={t(
                      'quote.scope_placeholder',
                      'What work needs to be done? Include details like size, materials, timeline expectations…'
                    )}
                    isInvalid={Boolean(errors.scopeDescription)}
                    errorMessage={errors.scopeDescription?.message}
                    classNames={{
                      input: 'text-sm',
                      label: 'text-sm font-semibold text-gray-700',
                      inputWrapper:
                        'border !border-gray-300 resize-none !bg-white hover:!border-gray-300 focus-within:!border-gray-300 focus-within:!bg-white outline-none focus:outline-none',
                    }}
                  />
                </div>

                {/* Location & Timeframe */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-700 block mb-1.5">
                      {t('quote.location', 'Where is the work location?')}
                    </label>
                    <div className="relative">
                      <CustomInput
                        type="text"
                        placeholder="City, full address, or remote"
                        {...register('location')}
                        startContent={
                          <FaMapLocationDot className="text-slate-400 shrink-0 mr-1" />
                        }
                      />
                    </div>
                    {errors.location && (
                      <p className="text-danger text-xs font-semibold mt-1">
                        {errors.location.message}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-700 block mb-1.5">
                      {t('quote.when', 'When do you need this done?')}
                      <span className="text-danger"> *</span>
                    </label>
                    <Select
                      {...register('timeframePreference')}
                      placeholder={t(
                        'quote.select_timeframe',
                        'Select timeframe'
                      )}
                      startContent={
                        <FaCalendarDays className="text-xs text-slate-400 shrink-0 mr-1" />
                      }
                      defaultSelectedKeys={['asap']}
                      className="text-xs"
                      size="sm"
                      classNames={{
                        trigger:
                          'bg-white hover:bg-white data-[hover=true]:bg-white data-[open=true]:bg-white border border-slate-200 shadow-sm',
                        value: 'text-slate-700', // optional: ensures the text color looks good on white
                      }}
                    >
                      {TIMEFRAME_OPTIONS.map((option) => (
                        <SelectItem key={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </Select>
                    {errors.timeframePreference && (
                      <p className="text-danger text-xs font-semibold mt-1">
                        {errors.timeframePreference.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Optional Budget Range */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-700 block mb-1.5">
                      {t('quote.budget_min', 'Budget Min (optional)')}
                    </label>
                    <CustomInput
                      type="number"
                      placeholder="CAD 0"
                      {...register('budgetMin')}
                    />
                    {errors.budgetMin && (
                      <p className="text-danger text-xs font-semibold mt-1">
                        {errors.budgetMin.message}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700 block mb-1.5">
                      {t('quote.budget_max', 'Budget Max (optional)')}
                    </label>
                    <CustomInput
                      type="number"
                      placeholder="CAD 0"
                      {...register('budgetMax')}
                    />
                    {errors.budgetMax && (
                      <p className="text-danger text-xs font-semibold mt-1">
                        {errors.budgetMax.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Attach Photo Uploads */}
                <div className="pt-2">
                  <label className="text-sm font-semibold text-slate-700 block mb-1.5">
                    {t(
                      'quote.add_photos',
                      'Attach Photos / Diagrams (optional)'
                    )}
                  </label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={(e) => handleFiles(e.target.files)}
                    disabled={uploading}
                    className="hidden"
                  />
                  <div className="flex flex-wrap items-center gap-3">
                    <CustomButton
                      label={`${t('quote.add_photos', 'Add photos')} (${photoUrls.length}/6)`}
                      variant="bordered"
                      startContent={<FaPlus size={12} />}
                      onClick={() => fileInputRef.current?.click()}
                      isLoading={uploading}
                      isDisabled={uploading || photoUrls.length >= 6}
                      className="border-slate-200 text-slate-700 hover:border-slate-300"
                    />
                    {uploading && (
                      <span className="text-xs text-slate-400 flex items-center gap-1.5 font-medium">
                        <Spinner size="sm" /> Uploading image to storage...
                      </span>
                    )}
                  </div>

                  {photoUrls.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-3">
                      {photoUrls.map((url, idx) => (
                        <div
                          key={idx}
                          className="group relative bg-slate-100 rounded-xl overflow-hidden h-20 border border-slate-200 shadow-2xs"
                        >
                          <img
                            src={url}
                            alt={`Photo ${idx + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              setPhotoUrls(
                                photoUrls.filter((_, i) => i !== idx)
                              )
                            }
                            className="absolute top-1 right-1 bg-rose-600/90 text-white rounded-full p-1 hover:bg-rose-700 transition-colors shadow-xs"
                          >
                            <FaXmark size={10} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-slate-100">
                  <CustomButton
                    label="Send Quote Requests to All Providers"
                    color="primary"
                    startContent={<FaPaperPlane size={14} />}
                    onPress={handleSubmit(onSubmit)}
                    isLoading={broadcastMutation.isPending}
                    isDisabled={broadcastMutation.isPending}
                    className="w-full font-bold py-3 text-sm shadow-md"
                  />
                </div>
              </form>
            </ModernCard>
          </div>

          {/* Right Sidebar Info (4 Cols) */}
          <div className="lg:col-span-4 space-y-6">
            <ModernCard
              title={
                <span className="font-bold text-slate-800">
                  {t('quote.what_happens_next', 'What happens next')}
                </span>
              }
            >
              <div className="space-y-4 text-xs text-slate-600 leading-relaxed">
                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/60">
                  <p className="font-bold text-slate-800 text-sm mb-1 flex items-center gap-1.5">
                    <span>🔔</span> {services.length}{' '}
                    {t('quote.providers_invited', 'Providers Selected')}
                  </p>
                  <p className="text-slate-600">
                    {t(
                      'quote.multi_quote_message',
                      'Your project description is dispatched simultaneously to all chosen providers.'
                    )}
                  </p>
                </div>

                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/60">
                  <p className="font-bold text-slate-800 text-sm mb-1 flex items-center gap-1.5">
                    <FaRegClock className="text-amber-500" size={14} />{' '}
                    {t('quote.response_time', 'Expected Response SLA')}
                  </p>
                  <p className="text-slate-600">
                    Providers reply within{' '}
                    <span className="font-bold text-slate-800">
                      {responseSlaDays} days
                    </span>
                    . You can compare incoming quotes live in your dashboard.
                  </p>
                </div>

                <div className="p-3 bg-slate-50 rounded-xl border border-slate-200/60 space-y-2">
                  <p className="font-bold text-slate-800 text-sm flex items-center gap-1.5">
                    <FaCircleCheck className="text-emerald-600" size={14} />{' '}
                    {t('quote.next_steps', 'Customer Workflow')}
                  </p>
                  <ul className="space-y-1.5 pl-1">
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      <span>
                        Compare itemized pricing & inclusions side-by-side
                      </span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      <span>Accept your preferred offer atomically</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      <span>Declined providers are notified automatically</span>
                    </li>
                  </ul>
                </div>
              </div>
            </ModernCard>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};

export default BatchQuoteRequestForm;
